import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ShoppingCart, ListChecks } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, imageUrl, formatEUR } from "../lib/api";
import { useCart } from "../lib/cart-context";

/**
 * Phase 22.1 — customer AI shopping-list generator page.
 *
 * Reachable at ``/lista-inteligente`` and linked from the mega-menu footer.
 * Sends the natural-language prompt to ``POST /api/ai/shopping-list`` and
 * renders the resulting list with checkboxes. Items that map to a real
 * MercaGo product show a product card and can be added to the cart with a
 * single "Añadir seleccionados al carro" action.
 */
export default function ShoppingListPage() {
  const [prompt, setPrompt] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const { addItem } = useCart();

  const generate = async (e) => {
    e?.preventDefault();
    if (prompt.trim().length < 5) {
      toast.error("Describe con un poco más de detalle.");
      return;
    }
    setBusy(true);
    setItems([]);
    setSelected(new Set());
    const t = toast.loading("IA armando tu lista…");
    try {
      const { data } = await api.post("/ai/shopping-list", { prompt: prompt.trim() });
      const raw = data.items || [];
      setItems(raw);
      // Preselect all items that have a real product match.
      const preset = new Set(
        raw.map((_, i) => i).filter((i) => raw[i].product_id),
      );
      setSelected(preset);
      toast.success(
        `${raw.length} artículos generados (${preset.size} disponibles en MercaGo)`,
        { id: t },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        detail === "ai_timeout"
          ? "El servicio de IA está tardando. Inténtalo de nuevo."
          : "IA no disponible en este momento.",
        { id: t },
      );
    } finally {
      setBusy(false);
    }
  };

  const toggle = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const addAll = async () => {
    const targets = items.filter((it, i) => selected.has(i) && it.product_id);
    if (targets.length === 0) {
      toast.error("No hay artículos disponibles en MercaGo.");
      return;
    }
    setAdding(true);
    let ok = 0;
    for (const it of targets) {
      try {
        await addItem(it.product_id, 1);
        ok += 1;
      } catch {
        /* ignore individual failures */
      }
    }
    setAdding(false);
    if (ok > 0) toast.success(`Añadidos ${ok} artículos al carro.`);
    else toast.error("No se pudo añadir ningún artículo.");
  };

  const availableCount = items.filter((it) => it.product_id).length;
  const unavailableCount = items.length - availableCount;

  return (
    <div
      data-testid="shopping-list-page"
      className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-2xl bg-[#F1FAF3] grid place-items-center">
          <ListChecks className="h-6 w-6 text-[#00953B]" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
            Genera tu lista con IA
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Cuéntanos qué necesitas y armamos la lista de la compra por ti.
          </p>
        </div>
      </div>

      <form onSubmit={generate} className="mt-6 space-y-3">
        <Textarea
          data-testid="shopping-list-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder='Ej: "compra para 4 personas para una semana, vegetariano" o "desayunos rápidos para niños de 8 años"'
          className="rounded-2xl border-neutral-200 text-base"
        />
        <div className="flex justify-end">
          <Button
            data-testid="shopping-list-generate-btn"
            type="submit"
            disabled={busy}
            className="h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-2 px-6"
          >
            <Sparkles className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} />
            {busy ? "Generando…" : "Generar lista"}
          </Button>
        </div>
      </form>

      {items.length > 0 && (
        <section
          data-testid="shopping-list-results"
          className="mt-8 rounded-2xl border border-neutral-200 bg-white p-4 sm:p-6"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <h2 className="text-xl font-black tracking-tight">
              Tu lista ({items.length})
            </h2>
            <div className="text-xs text-neutral-500">
              <span data-testid="shopping-list-available-count" className="text-[#00953B] font-semibold">
                {availableCount} en MercaGo
              </span>
              {unavailableCount > 0 && (
                <>
                  {" · "}
                  <span data-testid="shopping-list-unavailable-count">
                    {unavailableCount} sugerencia{unavailableCount === 1 ? "" : "s"}
                  </span>
                </>
              )}
            </div>
          </div>

          <ul className="divide-y divide-neutral-100">
            {items.map((it, i) => (
              <li
                key={i}
                data-testid={`shopping-list-item-${i}`}
                className="py-3 flex items-center gap-3"
              >
                <input
                  type="checkbox"
                  data-testid={`shopping-list-item-check-${i}`}
                  checked={selected.has(i)}
                  disabled={!it.product_id}
                  onChange={() => toggle(i)}
                  className="h-5 w-5 accent-[#00953B]"
                />
                {it.product ? (
                  <div className="h-12 w-12 rounded-lg bg-neutral-50 border border-neutral-100 shrink-0 overflow-hidden">
                    <img
                      src={imageUrl(it.product.image_url)}
                      alt={it.product.name}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-neutral-100 grid place-items-center text-xs text-neutral-400 shrink-0">
                    N/D
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {it.product ? (
                      <Link
                        to={`/producto/${it.product.slug}`}
                        className="font-semibold text-sm hover:text-[#00953B] truncate"
                      >
                        {it.product.name}
                      </Link>
                    ) : (
                      <span className="font-semibold text-sm text-neutral-600 truncate">
                        {it.name}
                      </span>
                    )}
                    <span className="text-xs text-neutral-500">
                      · {it.quantity}
                    </span>
                    {!it.product_id && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                        No disponible
                      </span>
                    )}
                  </div>
                  {it.reason && (
                    <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1">
                      {it.reason}
                    </div>
                  )}
                </div>
                <div className="text-sm font-semibold whitespace-nowrap">
                  {it.product?.price_eur != null
                    ? formatEUR(it.product.price_eur)
                    : "—"}
                </div>
              </li>
            ))}
          </ul>

          {availableCount > 0 && (
            <div className="mt-4 flex justify-end">
              <Button
                data-testid="shopping-list-add-all-btn"
                onClick={addAll}
                disabled={adding}
                className="h-12 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-2 px-6"
              >
                <ShoppingCart className="h-4 w-4" />
                {adding ? "Añadiendo…" : `Añadir seleccionados al carro (${selected.size})`}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
